import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useI18n } from "i18n";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import useCompanySettings from "modules/company/hooks/useCompanySettings";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { trackError } from "modules/monitoring";
import { IEvent } from "modules/event/types";
import { ICompany, CompanyPublicSettings } from "modules/company/types";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { LoginOptions } from "@remo-co/types";
import { useAppDispatch } from "store/hooks";
import actions from "modules/companySettings/redux/actions";
import { useSelector } from "react-redux";
import {
  selectInviteeEmail,
  selectInviteeRole,
} from "modules/auth/redux/selectors";
import { EventRole } from "modules/event/constants";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { EmailInput } from "./types";
import { useEventLandingPageParameters } from "../../hooks/useEventLandingPageParameters";
import { setShowRegisterDialog } from "../../redux/eventLandingSlice";
import { UserLoginOptions } from "./UserLoginOptions";
import { PrivateEventNudge } from "../PrivateEventNudge/PrivateEventNudge";
import { EventSpeakerNudge } from "../EventSpeakerNudge/EventSpeakerNudge";
import { styles } from "./styles";

interface Props {
  event: IEvent;
  email: EmailInput;
  setEmail: Dispatch<SetStateAction<EmailInput>>;
  setGoToLogin: Dispatch<SetStateAction<boolean>>;
  setShowUserDetailsForm: Dispatch<SetStateAction<boolean>>;
  setShowUserNameForm: Dispatch<SetStateAction<boolean>>;
}

export const UserInputContainer = ({
  event,
  email,
  setEmail,
  setGoToLogin,
  setShowUserDetailsForm,
  setShowUserNameForm,
}: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const { triggerAnonymousLogin } = useEventLandingPageParameters();
  const { checkIfEventHappeningNow, checkIfEventEnded } = useCheckEventStatus();
  const inviteeEmail = useSelector(selectInviteeEmail);
  const inviteeRole = useSelector(selectInviteeRole);
  const { t } = useI18n(["auth", "common", "eventLanding"]);

  const [eventCompanySettings, setEventCompanySettings] = useState<
    CompanyPublicSettings | undefined
  >(undefined);
  const { addErrorNotification } = useNotificationActions();
  const { getCompanyPublicSettings } = useCompanySettings();
  const [hasCompanySettingsLoaded, setHasCompanySettingsLoaded] =
    useState(false);

  useEffect(() => {
    const start = async () => {
      const companyId =
        typeof event.company === "string"
          ? event.company
          : (event.company as ICompany).id;

      try {
        const result = await getCompanyPublicSettings(companyId);

        setEventCompanySettings(result);
        dispatch(actions.setLoginOptions(result.loginOptions));

        setHasCompanySettingsLoaded(true);
        if (
          triggerAnonymousLogin &&
          !event.isPrivate &&
          result.loginOptions[LoginOptions.ANONYMOUS] &&
          checkIfEventHappeningNow(event)
        ) {
          setShowUserNameForm(true);
        }

        if (triggerAnonymousLogin && checkIfEventEnded(event)) {
          dispatch(setShowRegisterDialog(false));
        }
      } catch (error) {
        trackError(error);
        addErrorNotification({
          message: t("common:error.unknown"),
        });
      }
    };

    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="auth-reg-form">
      {!hasCompanySettingsLoaded ? (
        <Box sx={styles.container}>
          <PageLoader message={t("common:loading")} />
        </Box>
      ) : (
        <>
          {inviteeEmail && inviteeRole && inviteeRole === EventRole.speaker && (
            <EventSpeakerNudge inviteeEmail={inviteeEmail} />
          )}
          {(inviteeRole ? inviteeRole !== EventRole.speaker : true) &&
            event.isPrivate && (
              <PrivateEventNudge inviteeEmail={inviteeEmail} />
            )}
          <UserLoginOptions
            email={email}
            eventCompanySettings={eventCompanySettings}
            event={event}
            setEmail={setEmail}
            setGoToLogin={setGoToLogin}
            setShowUserDetailsForm={setShowUserDetailsForm}
            hasCompanySettingsLoaded={hasCompanySettingsLoaded}
            setShowUserNameForm={setShowUserNameForm}
          />
        </>
      )}
    </div>
  );
};
